/**
 * Grouping the approval queue by bulk submission, and wording what "Approve all" did. Pure.
 */
import type { ApprovalDecision, RoleProposal, SubmissionDecisionResponse } from '../../types/roleApprovals';

/** One card's worth of the queue: a single proposal, or every role of one bulk submission. */
export interface ProposalEntry {
  /** Set only when the entry is a real group — two or more roles sharing the id. */
  submissionId: string | null;
  proposals: RoleProposal[];
}

/**
 * The queue as cards, in the server's order (newest first). A group sits where its first role
 * would have, and its roles keep their relative order.
 *
 * A submission id with only ONE role in the queue renders as an ordinary card. A one-row bulk
 * submission is never stamped (the proxy skips it), but a sibling deleted in Airtable would leave
 * one behind, and a "group" of one would only add chrome around a single role.
 */
export function groupProposals(proposals: RoleProposal[]): ProposalEntry[] {
  const sizes = new Map<string, number>();
  proposals.forEach((p) => {
    if (p.submissionId) sizes.set(p.submissionId, (sizes.get(p.submissionId) ?? 0) + 1);
  });

  const entries: ProposalEntry[] = [];
  const groups = new Map<string, ProposalEntry>();
  for (const proposal of proposals) {
    const id = proposal.submissionId;
    if (!id || (sizes.get(id) ?? 0) < 2) {
      entries.push({ submissionId: null, proposals: [proposal] });
      continue;
    }
    const existing = groups.get(id);
    if (existing) {
      existing.proposals.push(proposal);
    } else {
      const entry = { submissionId: id, proposals: [proposal] };
      groups.set(id, entry);
      entries.push(entry);
    }
  }
  return entries;
}

const roles = (n: number) => `${n} role${n === 1 ? '' : 's'}`;

/**
 * What to tell the approver after a bulk decision: a notice when every role landed, an error naming
 * what did not. A role already decided is not a failure — "all" deliberately skips those.
 */
export function submissionDecisionNotice(
  result: SubmissionDecisionResponse,
  decision: Exclude<ApprovalDecision, null>
): { notice: string | null; error: string | null } {
  const verb = decision === 'Approved' ? 'Approved' : 'Declined';
  const landed = result.results.filter((r) => r.ok);
  const failed = result.results.filter((r) => !r.ok);
  // Locked roles (in the hiring plan, cancelled) are reported; ones you had already decided are not.
  const refused = result.skipped.filter((s) => s.reason !== 'already_decided');
  const inPlan = landed.filter((r) => r.transfer?.hiringPlanRecordId).length;
  const followUps = landed
    .map((r) => r.warning || (r.transfer && !r.transfer.ok ? r.transfer.message : null))
    .filter(Boolean);

  if (failed.length === 0 && refused.length === 0 && followUps.length === 0) {
    if (landed.length === 0) return { notice: 'Nothing to decide — you had already decided every role.', error: null };
    const tail = inPlan > 0 ? ` ${roles(inPlan)} now in the hiring plan as Backlog.` : '';
    return { notice: `${verb} ${roles(landed.length)}.${tail}`, error: null };
  }

  const parts = [];
  if (landed.length > 0) parts.push(`${verb} ${roles(landed.length)}.`);
  if (failed.length > 0) {
    parts.push(`${roles(failed.length)} could not be recorded: ${failed[0].error || 'Airtable refused it'}.`);
  }
  if (refused.length > 0) {
    parts.push(`${roles(refused.length)} skipped: ${refused[0].message || refused[0].reason}`);
  }
  parts.push(...(followUps as string[]));
  return { notice: null, error: parts.join(' ') };
}
